// Utensil detail/editor: aisle, synonyms, usage counts. Edit mode turns texts into fields in place.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { type DetailParams } from '../navigation';
import { useHeader } from '../hooks';
import { useAllUtensils, useLibrary } from '../store/library';
import { useShopping } from '../store/shopping';
import { Caption, Chip, EmptyState, IconButton, Row, Screen, Section } from '../components/ui';
import { EditableText, Fade } from '../components/Motion';
import { font, usePalette } from '../theme';
import { UTENSIL_AISLES, type Utensil } from '../types';
import { CONTENT_RECIPES } from '../../content';

export default function UtensilScreen({ route }: NativeStackScreenProps<DetailParams, 'Utensil'>) {
  const { t } = useTranslation();
  const p = usePalette();
  const all = useAllUtensils();
  const put = useLibrary(s => s.put);
  const recipes = useLibrary(s => s.recipes);
  const { grocery, pantry } = useShopping();
  const item = all[route.params.id];
  const [editing, setEditing] = useState(!!route.params.edit);
  const synonyms = item?.synonyms.join(', ') ?? '';
  const [synonymText, setSynonymText] = useState(synonyms);
  useEffect(() => {
    if (!editing) setSynonymText(synonyms);
  }, [editing, synonyms]);
  useHeader(
    { title: item?.title ?? '', headerRight: () => <IconButton icon={editing ? 'check' : 'edit'} label={editing ? t('common.done') : t('common.edit')} onPress={() => setEditing(e => !e)} /> },
    [editing, item?.title, t],
  );
  if (!item) return <EmptyState icon="utensil" title={t('common.empty')} />;
  const update = (patch: Partial<Utensil>) => put('utensils', { ...item, ...patch });
  const inRecipes = [...Object.values(recipes), ...Object.values(CONTENT_RECIPES)].filter(r => r.utensils.some(l => l.utensilId === item.id) || r.steps.some(s => s.utensils.some(l => l.utensilId === item.id))).length;
  const inGrocery = [...grocery.looseItems, ...grocery.recipes.flatMap(c => c.items)].filter(i => i.kind === 'utensil' && i.refId === item.id).length;
  const inPantry = pantry.items.filter(i => i.kind === 'utensil' && i.refId === item.id).length;

  return (
    <Screen>
      <EditableText editing={editing} value={item.title} onChangeText={v => update({ title: v.replace(/\n/g, ' ') })} accessibilityLabel={t('editors.title')}
        style={{ fontSize: font.title, fontWeight: '700', color: p.text, lineHeight: 34 }} />
      {item.builtIn ? <Fade visible={editing}><Caption>{t('editors.builtIn')}</Caption></Fade> : null}
      <Section title={t('editors.aisle')}>
        <Row>
          {UTENSIL_AISLES.map(a => (
            <Chip key={a} label={t(`aisles.${a}`)} selected={item.aisle === a} onPress={editing ? () => update({ aisle: a }) : undefined} />
          ))}
        </Row>
      </Section>
      <Section title={t('editors.synonyms')}>
        <EditableText
          editing={editing}
          value={editing ? synonymText : synonyms || '—'}
          onChangeText={v => {
            setSynonymText(v);
            update({ synonyms: v.split(/[,\n]/).map(s => s.trim()).filter(Boolean) });
          }}
          placeholder="—"
          style={{ fontSize: font.body, color: p.secondaryText, lineHeight: 22 }}
        />
      </Section>
      <Section title={t('editors.usage')}>
        <Caption>{t('editors.usedInRecipes', { count: inRecipes })}</Caption>
        <Caption>{t('editors.groceryCount', { count: inGrocery })} · {t('editors.pantryCount', { count: inPantry })} · {t('editors.synonymsCount', { count: item.synonyms.length })}</Caption>
      </Section>
    </Screen>
  );
}
